import { ConnectionAnalyser } from "./connectionAnalyser/ConnectionAnalyser";
import BaseModel from "./models/BaseModel";
import BaseModelMap from "./models/BaseModelMap";
// elements
import Floor from "./models/Floor";
import Cabinet from "./models/Cabinet";
import Device from "./models/Device";
import Actor from "./models/Actor";
import type Button from "./models/Button";
import Channel from "./models/Channel";
import Scene from "./models/Scene";

import AllConnections from "./models/AllConnections";
import PageGroup from "./models/PageGroup";
import Page from "./models/Page";
import ActivePanes from "./models/ActivePanes";
import { compareProperties, sortArrayByProperty } from "./models/utils";
import WatchableValue from "./models/WatchableValue";
import GroupAddress from "./models/GroupAdress";
import ObjectRegistry from "./models/ObjectRegistry";
import Root from "./models/Root";
import ObjectMapping from "./models/ObjectMapping";
import Settings from "./models/Settings";
import { C1_Project } from "../ConceptOne/src/model/C1_Project";
import type { C1_State } from "../ConceptOne/src/model/C1_State";
import { createC1State } from "../ConceptOne/src/model/C1_runtimeContext";
import { C1_Axis } from "../ConceptOne/src/model/C1_Axis";
import TemplateGroup, { createMissingTemplateGroups } from "./models/TemplateGroup";
import ProjectMetadata from "./models/ProjectMetadata";

import PageReference from "./models/PageReference";

import CurrentDrawingConnection from "./CurrentDrawingConnection";

import DialogManager from "./DialogManager";
import ContextMenuManager from "./ContextMenuManager";

import PageGroupsTreeViewModel from "./modelTreeViews/PageGroupsTreeviewModel";
import TemplateGroupsTreeViewModel from "./modelTreeViews/TemplateGroupsTreeviewModel";
import CabinetsTreeViewModel from "./modelTreeViews/CabinetsTreeviewModel";
import GroupAddressesTreeViewModel from "./modelTreeViews/GroupAddressesTreeviewModel";
import FloorsTreeViewModel from "./modelTreeViews/FloorsTreeviewModel";
import SceneGroupTreeViewModel from "./modelTreeViews/SceneGroupTreeviewModel";

import UndoManager from "./undomanager/UndoManager";
import SelectionManager from "./SelectionManager";
import DockManager from "./DockManager";
import { KnxMasterData, loadKnxMasterData } from "./KnxProductDataTools/LoadKnxMasterData";
import axios from "./utils/axios";
import NoDoubleValues from "./components/NoDoubleValues";

import MenubarActions from "./components/MenubarActions";

import { NavigateFunction } from "react-router-dom";

import Clipboard from "./components/Clipboard";

import { EventLocker } from "./eventLogger/EventLocker";

import TranslateValueFunctions from "./translationItx/TranslateValueFunctions";
import Logger from "./Logger";
import {
  enumUiMode,
  getMaxUiMode,
  isUiModeDeveloper,
  isUiModeExpert,
  isUiModeProductAdmin,
  setUiMode_XX,
} from "./uiMode";
import BackgroundImmageHandler from "./components/BackgroundImmageHandler";
import { showErrToast, showToast } from "./components/Toasts";
import Browser from "./browser";
import type Room from "./models/Room";
import TopologyLevel from "./models/Line";
import TopologiesTreeViewModel from "./modelTreeViews/TopologiesTreeviewModel";
import type ELCB from "./models/ELCB";
import type Fuse from "./models/Fuse";
import NameAndTagManager from "./NameAndTagManager";
import type Consumer from "./models/Consumer";
import { ChannelType, channelTypeInfo } from "./models/ChannelType";
import type TerminalStrip from "./models/TerminalStrip";
import type Terminal from "./models/Terminal";
import BaseObject, { Event, newId } from "./models/BaseObject";

import { T, noT } from "./translationItx/itx";
import OnBoadingManager from "./onBoarding/OnBoardingManager";
import ConnectableObject from "./models/ConnectableObject";
import GuideMode from "./onBoarding/GuideMode";
import { CableType, initCableTypes } from "./models/CableType";
import CableTypesTreeViewModel from "./modelTreeViews/CableTypesTreeviewModel";
import type TabItemData from "./models/TabItemData";
import { isDevelopmentEnvironment } from "./estada-config";
import type Signal from "./models/Signal";
import { initSignalBundleDefinitions } from "./models/SignalBundleDefinitions";
import TaskAndMessageManager from "./TaskAndMessageManager";
import DebugOutput from "./DebugOutput";
import ObjectSet from "./models/ObjectSet";
import ObjectSetsTreeviewModel from "./modelTreeViews/ObjectSetsTreeviewModel";
import { enumAvailableLanguages } from "./models/SettingsEnums";
import { initAvailiableLanguages } from "./components/AvailiableLanguages";
import { AboHandeling } from "./components/Abohandeling";
import Point from "./models/Point";
import Area from "./models/Area";
import { ConsistencyErrorLogEntry } from "./models/ConsistencyErrorLogEntry";
import { showConsitencyErrorsDialog } from "./components/ConsitencyErrorsDialog";
import DeviceTemplate from "./models/DeviceTemplate";
import AutobackupStorageHandler from "./persistence/AutobackupStorageHandler";
import Multiplexer from "./models/Multiplexer";
import ProjectLicense from "./models/ProjectLicense";

const logger = new Logger();

declare global {
  var state: State;
  var compareState: State | undefined;
}

export enum ConnectionDisplayModes {
  all = "all", // no itx
  channels = "channels", // no itx
  scenes = "scenes", // no itx
  buttons = "buttons", // no itx
  none = "none",
}

type Lo = {
  track: (text: string) => void;
};

export class State {
  readonly connectionAnalyser = new ConnectionAnalyser();
  id = "";

  eventLogger = new EventLocker();

  _isLoggedIn = false;
  set isLoggedIn(newValue: boolean) {
    this._isLoggedIn = newValue;
  }
  get isLoggedIn() {
    return this._isLoggedIn;
  }

  projectMetadata = new ProjectMetadata();

  _navigate: NavigateFunction | undefined = undefined;
  navigationParams: any;
  orgTranslate: (key: string, defaultValue: string) => string = (x: string, y: string) => {
    logger.log("translate not declared but used-> value ", x);
    return y;
  };

  notranslate(value: string) {
    return noT(value);
  }

  translate(key: string, defaultValue?: string, vars?: any): string {
    return T(key, defaultValue, vars);
  }

  get installationMode(): boolean {
    return state.settings.diagnosticMode;
  }
  set installationMode(newValue: boolean) {
    state.settings.diagnosticMode = newValue;
  }

  browserName = "";
  debugMode: boolean = false;
  coverPage: Page | undefined;

  mobileChecked = false;
  etsIsOnline: boolean = false;

  tabItems = new Array<TabItemData>();
  tabItems2 = new Array<TabItemData>();

  onlineOfflineMode = new WatchableValue<"online" | "offline" | "">(""); // no itx
  flyoutMenuVisible = new WatchableValue<string>("");

  navigate(url: string | number, params?: any) {
    this.navigationParams = params;
    if (this._navigate) {
      if (url == "/") {
        this.stopAutobackupInterval();
        state.projectMetadata.clear();
      }
      this._navigate(url as any);
    }
  }

  flattenGroups = new WatchableValue(false, "flattenGroups");

  isLoadingFromEstadaStorage = false;
  // Getter/setter changes must not be observed while the initial State is built
  // or while persistent project data is restored. Observation is enabled for
  // the first time after a project has finished loading.
  suppressUndoObservation = true;
  isSaveingToEstadaStorage = false;

  updateProject = false;
  objectRegistry = new ObjectRegistry();
  // during paste this objecteRegistry is the one of the targetProject we are pasting to
  targetObjectRegistry = new ObjectRegistry();

  settings = new Settings();
  private _c1?: C1_State;

  /** Lazily creates the Concept One state owned by this application state. */
  get c1(): C1_State {
    if (!this._c1) this._c1 = createC1State(this);
    return this._c1;
  }

  activePanes = new ActivePanes(false);
  activePanes2: ActivePanes | undefined;

  get activePanesOfCurrentScreen(): ActivePanes {
    if (this.activeDocumentScreen == 2) {
      return this.activePanes2 || this.activePanes;
    } else {
      return this.activePanes;
    }
  }

  get activePaneId(): string {
    if (this.activeDocumentScreen == 2) {
      return this.activePanes2?.getActiveId() || "";
    } else {
      return this.activePanes.getActiveId() || "";
    }
  }

  window2: any = undefined;
  documentScreen2: Document | undefined = undefined;
  documentScreen1: Document | undefined = undefined;
  activeDocumentScreen: number = 1;

  root = new Root({ id: this.projectMetadata.id });

  // data that should be persistent--------------------------------
  // devices = new Devices(Device);

  projectLicense = new ProjectLicense({}, this.root);

  pageGroups = new BaseModelMap<PageGroup>(PageGroup, this.root, "p");
  floors = new BaseModelMap<Floor>(Floor, this.root, "f");
  axes = new BaseModelMap<C1_Axis>(C1_Axis, this.root, "c1-axes");

  cabinets = new BaseModelMap<Cabinet>(Cabinet, this.root, "cab"); // no itx

  templateGroups = new BaseModelMap<TemplateGroup>(TemplateGroup, this.root, "tg"); // no itx
  c1Project = new C1_Project({}, this.templateGroups);

  topologies = new BaseModelMap<Area>(Area, this.root, "tl"); // non persitent data !!!

  cableTypes = new BaseModelMap<CableType>(CableType, this.root, "ctyp"); // no itx

  connectionDisplayMode = new WatchableValue<ConnectionDisplayModes>(ConnectionDisplayModes.all);
  allConnections = new AllConnections();

  clipboardItems: BaseModelMap<BaseModel> | undefined;
  objectMapping = new ObjectMapping();

  selectionManager = new SelectionManager();
  readonly dockManager = new DockManager(this);
  nameAndTagManager = new NameAndTagManager();

  currentDrawingConnection = new CurrentDrawingConnection();
  addresses = new BaseModelMap<GroupAddress>(GroupAddress, this.root, "a");
  // sceneGroups = new BaseModelMap<SceneGroup>(SceneGroup, this.root, "sg");

  sceneGroup = new BaseModelMap<Scene>(Scene, this.root, "sg"); // no itx

  objectSets = new BaseModelMap<ObjectSet>(ObjectSet, this.root, "os"); // no itx

  // end of persistent data-----------------------------------------
  dialogManager = new DialogManager();
  taskAndMessageManager = new TaskAndMessageManager();
  onBoadingManager = new OnBoadingManager();
  contextMenuManager = new ContextMenuManager();
  noDoubleValue = new NoDoubleValues();
  translateValueFunctions = new TranslateValueFunctions();
  // bumped after every UI language switch to force all tree nodes to re-render with fresh translations
  languageVersion = new WatchableValue<number>(0);

  undoManager = new UndoManager();

  menubarActions = new MenubarActions();
  backgroundImmageHandler = new BackgroundImmageHandler();
  clipboard = new Clipboard();

  dragData: any = undefined;
  dragEffectAllowed: string = "";

  _knxMasterData: KnxMasterData | undefined = undefined;
  allProjectsMap = new Map<string, ProjectMetadata>();
  //allProjects = new Array<ProjectMetadata>();

  knxState = new WatchableValue<"active" | "error" | "unkown">("unkown");
  knxStateText = new WatchableValue<string>("");

  guideMode = new GuideMode();

  browser = Browser.getBrowser();
  maxCharLimit = {
    tag: 10,
    address: 11,
    text: 64,
    smallText: 32,
    largeText: 256,
    paragraph: 512,
  };
  availiableLanguages = new Array<enumAvailableLanguages>();

  treeSearchState = new WatchableValue<boolean>(false);
  connectionDashboardMode = new WatchableValue(false, "connectionDashboardMode");
  readonly mobileEditEnabled = new WatchableValue(false, "mobileEditEnabled");
  readonly mobileConnectionsEnabled = new WatchableValue(false, "mobileConnectionsEnabled");
  treeSearchString = new WatchableValue<string>("");

  programStat = {
    numberOfProgrammedProcesses: 0, // Net Total of Programming processes that modifies any physical device (excluding device verification)
    numberOfProgrammedDevices: 0, // Unique Devices that are programmed (Any of 6 program modes: Unload, Download Param, Download and Verify, Partial Download, Overwrite Addr and Download Addr)
    allProgrammingOperations: 0, // All Operations via Programming interface (also includes Device Search and Memory Verification)
    operationInProgress: false,
  };

  currentEditObject = new WatchableValue<ConnectableObject | undefined>(undefined, "currentEditObject"); // no itx
  currentEditObjectPosition = new WatchableValue<Point>(new Point(0, 0), "currentEditObjectPosition"); // no itx
  showButtonChannels = new WatchableValue<{ button: Button } | undefined>(undefined, "showButtonChannels"); // no itx

  private _showLoader = true;
  public get showLoader() {
    return this._showLoader;
  }
  public set showLoader(value) {
    this._showLoader = value;
  }
  showMenubarGroup_tools = new WatchableValue<boolean>(false);

  etsSyncedProjectIds: string[] = [];

  resetTreeSearchState() {
    this.treeSearchState.value = false;
    this.treeSearchString.value = "";
  }

  async loadKnxMasterData(): Promise<KnxMasterData> {
    if (this._knxMasterData == undefined) {
      this._knxMasterData = await loadKnxMasterData();
    }
    return this._knxMasterData;
  }

  constructor() {
    this.id = newId();

    globalThis.state = this;

    console.log("constructor state");

    this.floors.name = "Etagen"; // no itx
    this.floors.tag = "01";
    this.floors.storeInObjectRegistry = true;
    this.floors.treeViewModel = new FloorsTreeViewModel(this.floors);

    this.axes.name = "Concept-One-Achsen";
    this.axes.storeInObjectRegistry = true;

    this.cabinets.name = "Schaltschränke"; // no itx
    this.cabinets.tag = "02";
    this.cabinets.storeInObjectRegistry = true;
    this.cabinets.treeViewModel = new CabinetsTreeViewModel(this.cabinets);

    this.templateGroups.name = "Produkte"; // no itx
    this.templateGroups.tag = "03";
    this.templateGroups.storeInObjectRegistry = true;
    this.templateGroups.treeViewModel = new TemplateGroupsTreeViewModel(this.templateGroups);

    this.addresses.name = "Knx Adressen"; // no itx
    this.addresses.tag = "04";
    this.addresses.storeInObjectRegistry = true;
    this.addresses.treeViewModel = new GroupAddressesTreeViewModel(this.addresses);

    this.sceneGroup.name = "Szenen & Multiplexer"; // no itx
    this.sceneGroup.tag = "05";
    this.sceneGroup.storeInObjectRegistry = true;
    this.sceneGroup.treeViewModel = new SceneGroupTreeViewModel(this.sceneGroup);

    this.pageGroups.name = "Dokumente"; // no itx
    this.pageGroups.tag = "06";
    this.pageGroups.storeInObjectRegistry = true;
    this.pageGroups.treeViewModel = new PageGroupsTreeViewModel(this.pageGroups);

    this.topologies.name = "Topologie";
    this.topologies.tag = "07";
    this.topologies.storeInObjectRegistry = true; // mwe 29.4.205 fix Navigation in NavigationTreeview
    this.topologies.isPersistent = false;
    this.topologies.treeViewModel = new TopologiesTreeViewModel(this.topologies);

    this.cableTypes.name = "Kabeltypen"; // no itx
    this.cableTypes.tag = "08";
    this.cableTypes.storeInObjectRegistry = true;
    this.cableTypes.treeViewModel = new CableTypesTreeViewModel(this.cableTypes);

    this.objectSets.name = "ObjektSets"; // no itx
    this.objectSets.tag = "09";
    this.objectSets.storeInObjectRegistry = true;
    this.objectSets.treeViewModel = new ObjectSetsTreeviewModel(this.objectSets);

    initCableTypes();

    initAvailiableLanguages();

    if (location.host.toLowerCase().indexOf("localhost") >= 0) {
      this.debugMode = true;
    }

    UndoManager.init();

    const s = window.localStorage.getItem("onlineOfflineMode");
    if (s == "offline" || s == "online") {
      this.onlineOfflineMode.value = s;
    } else {
      this.onlineOfflineMode.value = "online"; // no itx
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////

  async saveToEstadaStorage(
    saveItem: (item: BaseModel) => Promise<boolean>,
    showErrors = true,
    clearModifiedStatus = true,
  ) {
    this.isSaveingToEstadaStorage = true;

    this.consistencyCheck(showErrors);

    this.objectRegistry.clearIsSaved();

    this.projectLicense.id = this.projectMetadata.id + `-license`;
    await this.projectLicense.saveToEstadaStorage(undefined, saveItem, this.root);

    await this.floors.saveToEstadaStorage(undefined, saveItem);
    await this.axes.saveToEstadaStorage(undefined, saveItem);
    await this.cabinets.saveToEstadaStorage(undefined, saveItem);
    await this.templateGroups.saveToEstadaStorage(undefined, saveItem);
    await this.addresses.saveToEstadaStorage(undefined, saveItem);
    // await this.sceneGroups.saveToEstadaStorage(undefined, saveItem);
    await this.sceneGroup.saveToEstadaStorage(undefined, saveItem);
    await this.pageGroups.saveToEstadaStorage(undefined, saveItem);

    await this.cableTypes.saveToEstadaStorage(undefined, saveItem);

    await this.objectSets.saveToEstadaStorage(undefined, saveItem);

    this.settings.id = this.projectMetadata.id + `-s`;
    await this.settings.saveToEstadaStorage(undefined, saveItem, this.root);

    if (this.projectMetadata.isC1Project) {
      this.c1Project.id = this.projectMetadata.id + `-c1`;
      await this.c1Project.saveToEstadaStorage(undefined, saveItem, this.root);
    }

    if (clearModifiedStatus) {
      this.clearModifiedStatus();
    }

    if (isDevelopmentEnvironment()) {
      this.objectRegistry.allObjects.forEach((obj) => {
        if (!obj.isSaved && obj.className != "BaseModelMap" && obj.className != "ProjectMetadata" && obj.isPersistent) {
          logger.log(`saveToEstadaStorage object not saved: ${obj.className}`, obj);
        }
      });
    }

    this.isSaveingToEstadaStorage = false;

    return true;
  }

  clearDirty() {
    logger.log("clear dirty");

    this.projectMetadata.clearDirty();
    this.objectRegistry.allObjects.forEach((obj) => {
      obj.clearDirty();
      if (this.showModifiedStatus) {
        delete obj.dirtyChilds;
        obj.raiseOnChangeUI();
      }
    });

    this.floors.clearDirtyChilds();
    this.axes.clearDirtyChilds();
    this.cabinets.clearDirtyChilds();
    this.templateGroups.clearDirtyChilds();
    this.addresses.clearDirtyChilds();
    this.sceneGroup.clearDirtyChilds();
    this.pageGroups.clearDirtyChilds();
    this.cableTypes.clearDirtyChilds();

    if (this.showModifiedStatus) {
      this.floors.raiseOnChangeUI();
      this.axes.raiseOnChangeUI();
      this.cabinets.raiseOnChangeUI();
      this.templateGroups.raiseOnChangeUI();
      this.addresses.raiseOnChangeUI();
      this.sceneGroup.raiseOnChangeUI();
      this.pageGroups.raiseOnChangeUI();
      this.cableTypes.raiseOnChangeUI();
    }
  }

  autobackupNeeded = false;

  get isDirty(): boolean {
    if (this.projectMetadata.isDirty) {
      logger.log(`isDirty: metadata`);
      return true;
    }

    for (const obj of this.objectRegistry.allObjects.values()) {
      if (obj.isDirty) {
        logger.log(`isDirty: ${obj.className} ${obj.tag} ${obj.name}`);
        return true;
      }
    }

    return false;
  }

  consistencyCheckLog = new Array<ConsistencyErrorLogEntry>();
  newInconsitencyErrors = false;

  logConsistencyError(obj: BaseModel | undefined, message: string, userError?: string, action?: () => void) {
    this.consistencyCheckLog.push({ id: obj?.id || "", message, userError: userError || "", action });
  }

  consistencyCheck(showErrors = true) {
    this.consistencyCheckLog = [];

    this.floors.consistencyCheck(this.floors);
    this.axes.consistencyCheck(this.axes);
    this.cabinets.consistencyCheck(this.cabinets);
    this.templateGroups.consistencyCheck(this.templateGroups);

    GroupAddress.checkDuplicateGroupAddresses(this.addresses);
    this.addresses.consistencyCheck(this.addresses);

    this.sceneGroup.consistencyCheck(this.sceneGroup);
    this.pageGroups.consistencyCheck(this.pageGroups);
    this.cableTypes.consistencyCheck(this.cableTypes);
    this.topologies.consistencyCheck(this.topologies);

    if (showErrors && this.consistencyCheckLog.length > 0) {
      showConsitencyErrorsDialog(this.consistencyCheckLog);
    }

    this.newInconsitencyErrors = false;
  }

  updateAllErrors() {
    //
    // update once errorInChildObjects
    //
    let allErrorObjects = new Array<BaseModel>();
    allErrorObjects = allErrorObjects.concat(this.pageGroups.findAllErrorInChildObjects());
    allErrorObjects = allErrorObjects.concat(this.floors.findAllErrorInChildObjects());
    allErrorObjects = allErrorObjects.concat(this.cabinets.findAllErrorInChildObjects());
    allErrorObjects = allErrorObjects.concat(this.templateGroups.findAllErrorInChildObjects());
    allErrorObjects = allErrorObjects.concat(this.sceneGroup.findAllErrorInChildObjects());
    allErrorObjects = allErrorObjects.concat(this.addresses.findAllErrorInChildObjects());
    allErrorObjects = allErrorObjects.concat(this.cableTypes.findAllErrorInChildObjects());

    if (allErrorObjects.length > 0) {
      allErrorObjects = sortArrayByProperty(allErrorObjects, "fullTag");
      console.log("Errors found in project:");
      allErrorObjects.forEach((obj) => {
        console.log(`${obj.fullTag}: ${obj.errorsAsString}`);
      });
    }
  }

  async loadFromEstadaStorage(
    projectId: string,
    loadData: (id: string | undefined, parentId: string | undefined) => Promise<any>,
  ): Promise<boolean> {
    BaseObject.suppresMarkAsDirty = true;
    let projectLoaded = false;
    this.suppressUndoObservation = true;
    try {
      this.projectMetadata.id = projectId;

      this.clear();

      this.updateProject = true;

      this.isLoadingFromEstadaStorage = true;

      this.projectLicense.id = this.projectMetadata.id + `-license`;
      await this.projectLicense.loadFromEstadaStorage(undefined, this.projectLicense, loadData);

      await this.pageGroups.loadFromEstadaStorage(undefined, this.pageGroups, loadData);

      await this.axes.loadFromEstadaStorage(undefined, this.axes, loadData);
      await this.floors.loadFromEstadaStorage(undefined, this.floors, loadData);
      //! SIGNALS EXIST AFTER LOADING FLOORS

      await this.cabinets.loadFromEstadaStorage(undefined, this.cabinets, loadData);

      await this.templateGroups.loadFromEstadaStorage(undefined, this.templateGroups, loadData);

      // await this.sceneGroups.loadFromEstadaStorage(
      //   undefined,
      //   this.sceneGroups,
      //   loadData,
      // );

      await this.sceneGroup.loadFromEstadaStorage(undefined, this.sceneGroup, loadData);

      await this.pageGroups.loadFromEstadaStorage(undefined, this.pageGroups, loadData);

      await this.addresses.loadFromEstadaStorage(undefined, this.addresses, loadData);

      await this.cableTypes.loadFromEstadaStorage(undefined, this.cableTypes, loadData);

      await this.objectSets.loadFromEstadaStorage(undefined, this.objectSets, loadData);

      this.settings.id = this.projectMetadata.id + `-s`;
      this.settings.masterPdfConfig = undefined;
      this.settings.connectionDashboardVisibleTrees = undefined;
      await this.settings.loadFromEstadaStorage(undefined, this.settings, loadData);

      this.c1Project = new C1_Project({}, this.templateGroups);
      if (this.projectMetadata.isC1Project) {
        this.c1Project.id = this.projectMetadata.id + `-c1`;
        await this.c1Project.loadFromEstadaStorage(undefined, this.c1Project, loadData);
      }

      createMissingTemplateGroups(this.templateGroups, this.projectMetadata.isC1Project);

      this.updateAllConnectionsWithoutUpdate();

      initCableTypes();

      initSignalBundleDefinitions();

      this.initRelations();

      this.updateAreas(true);

      this.consistencyCheck();
      this.checkPhysicalAdresses();

      this.updateAllErrors();

      this.clearDirty();

      this.pageGroups.raiseOnChangeUI();
      this.floors.raiseOnChangeUI();
      this.cabinets.raiseOnChangeUI();
      this.templateGroups.raiseOnChangeUI();
      this.sceneGroup.raiseOnChangeUI();
      this.pageGroups.raiseOnChangeUI();
      this.addresses.raiseOnChangeUI();
      this.cableTypes.raiseOnChangeUI();

      await this.settings.loadDefaultCompanyLogo();
      await this.settings.calcCompanyLogoSize();
      projectLoaded = true;
    } catch (e: any) {
      showErrToast(`Error loading project ${e.msg}`);
      logger.error(e);
    }

    if (this.undoManager.hasUndoEntries) {
      logger.error("Undo entries were created while loading the project");
    }
    this.undoManager.clear();
    this.enableUndoMonitoring();
    this.isLoadingFromEstadaStorage = false;
    this.suppressUndoObservation = false;

    BaseObject.suppresMarkAsDirty = false;

    if (projectLoaded) {
      this.startAutobackupInterval();
    }

    return projectLoaded;
  }

  private autobackupStorageHandler: AutobackupStorageHandler | undefined;
  private autobackupInterval: ReturnType<typeof setInterval> | undefined;
  private autobackupInProgress = false;
  private autobackupCompletion: Promise<void> | undefined;
  _lastAutobackupCreatedAt = new WatchableValue<number | undefined>(undefined);

  get lastAutobackupCreatedAt(): number | undefined {
    return this._lastAutobackupCreatedAt.value;
  }

  clearAutobackupStatus(): void {
    this._lastAutobackupCreatedAt.value = undefined;
  }

  private stopAutobackupInterval(): void {
    if (this.autobackupInterval) {
      clearInterval(this.autobackupInterval);
      this.autobackupInterval = undefined;
    }

    this.autobackupNeeded = false;
  }

  async deleteActiveAutobackups(): Promise<boolean> {
    this.stopAutobackupInterval();
    await this.autobackupCompletion;
    this.autobackupStorageHandler ??= new AutobackupStorageHandler();
    const deleted = await this.autobackupStorageHandler.deleteActiveAutobackups(this.projectMetadata.id);
    if (deleted) this.clearAutobackupStatus();
    return deleted;
  }

  private startAutobackupInterval(): void {
    this.stopAutobackupInterval();
    this.clearAutobackupStatus();

    this.autobackupInProgress = false;
    this.autobackupInterval = setInterval(
      () => {
        if (this.autobackupInProgress || !this.autobackupNeeded) return;

        void this.createAutobackup();
        this.autobackupNeeded = false;
      },
      2 * 60 * 1000,
    );
  }

  async createAutobackup(): Promise<boolean> {
    const startedAt = performance.now();
    this.autobackupStorageHandler ??= new AutobackupStorageHandler();
    this.autobackupInProgress = true;
    let resolveCompletion!: () => void;
    const completion = new Promise<void>((resolve) => {
      resolveCompletion = resolve;
    });
    this.autobackupCompletion = completion;
    const markCompleted = () => {
      resolveCompletion();
      if (this.autobackupCompletion === completion) this.autobackupCompletion = undefined;
    };

    try {
      const sent = await this.autobackupStorageHandler.startProjectBackup(
        this.projectMetadata.id,
        this.projectMetadata,
        false,
        (success) => {
          if (!success) {
            logger.error("Autobackup could not be saved");
            this.autobackupInProgress = false;
            markCompleted();
            return;
          }

          this.autobackupInProgress = false;
          this._lastAutobackupCreatedAt.value = Date.now();
          markCompleted();

          if (false && isDevelopmentEnvironment()) {
            const durationSeconds = ((performance.now() - startedAt) / 1000).toFixed(2);
            void showToast({
              text: `Autobackup in ${durationSeconds} Sek. durchgeführt`,
              isSuccess: true,
              modal: false,
            });
          }
        },
      );

      if (!sent) {
        logger.error("Autobackup could not be sent to worker");
        this.autobackupInProgress = false;
        markCompleted();
        return false;
      }
      return true;
    } catch (error) {
      logger.error("Autobackup could not be saved", error);
      this.autobackupInProgress = false;
      markCompleted();
      return false;
    }
  }

  enableUndoMonitoring() {
    this.objectRegistry.allObjects.forEach((object) => object.setMonitorForUndo(true));
  }

  initRelations() {
    this.objectRegistry.allObjects.forEach((obj) => {
      try {
        obj.initRelations();
      } catch (ex) {
        logger.error("Error in initRelations", ex);
      }
    });
  }
  checkPhysicalAdresses() {
    const a = new Map<string, Device>();

    this.allActors().forEach((dev) => {
      if (channelTypeInfo(dev.channelType).isSupply) {
        dev.areaAddress = "0";
        dev.lineAddress = "0";
        dev.physicalAddress = "0";

        dev.flagErrors({ doublePhysicalAdresses: "" }, false);
      }
    });

    this.allDevices().forEach((dev) => {
      if (!channelTypeInfo(dev.channelType).isSupply && dev.physicalAddress !== "-") {
        const errDev = a.get(dev.fullPhysicalAddress);

        dev.flagErrors({ doublePhysicalAdresses: "" }, errDev != undefined);
        errDev?.flagErrors({ doublePhysicalAdresses: "" }, true);
        a.set(dev.fullPhysicalAddress, dev);
      }
    });
  }

  ///////////////////////////////////////////////////////////////////////////////////////////

  toJson(): any {
    return {
      // TODO: diese 3 Werte sind in project gedoppelt
      metadata: {
        id: this.projectMetadata.id,
        name: this.projectMetadata.name,
        description: this.projectMetadata.description,
      },

      pageGroups: this.pageGroups.toJSON(),
      floors: this.floors.toJSON(),
      axes: this.axes.toJSON(),
      cabinets: this.cabinets.toJSON(),
      templateGroups: this.templateGroups.toJSON(),
      addresses: this.addresses.toJSON(),
      project: this.projectMetadata.toJSON(),
      //sceneGroups: this.sceneGroups.toJSON(),
      sceneGroups: this.sceneGroup.toJSON(),
      settings: this.settings.toJSON(),
      update: this.updateProject,
    };
  }

  compareTo(other: State): string[] {
    const fields = [
      "pageGroups", // no itx
      "floors", // no itx
      "cabinets", // no itx
      "templateGroups", // no itx
      //"sceneGroups",  // no itx
      "sceneGroup", // no itx
    ];
    return compareProperties(this, other, ...fields); // no itx
  }

  private clearProjectMap(collection: BaseModelMap<any>) {
    collection.clear();
    collection.clearDeletedChildCollections();
    collection.clearLastItems();
    delete collection.newInOtherProjectItems;
  }

  clear() {
    this.settings.c1TagByIndex = undefined;
    this.settings.c1TagEditable = undefined;
    this.updateProject = false;

    this.root.id = this.projectMetadata.id;

    this.activePanes2?.dispose();
    this.activePanes2 = undefined;
    this.window2 = undefined;
    this.documentScreen2 = undefined;
    (window as any).secondScreenWindow?.close();
    delete (window as any).secondScreenWindow;

    this.objectRegistry.clear();
    this._c1?.registerRuntimeObjects();
    this.targetObjectRegistry.clear();
    this.objectMapping = new ObjectMapping();
    this.clipboardItems?.clear();
    this.clipboardItems = undefined;
    this.currentDrawingConnection.clear();
    this.coverPage = undefined;
    this.dragData = undefined;
    this.dragEffectAllowed = "";
    this.currentEditObject.value = undefined;
    this.showButtonChannels.value = undefined;
    this.consistencyCheckLog = [];
    this.newInconsitencyErrors = false;
    this.guideMode.clear();

    //////////////////////////////////////////

    this.selectionManager.clear();
    this.selectionManager.editorPins.clear();
    this.selectionManager.displaySelections.clear();
    this.undoManager.clear();
    this.dialogManager.clear();
    this.contextMenuManager.clear();
    this.activePanes.clear();

    //////////////////////////////////////////

    this.clearProjectMap(this.pageGroups);
    this.clearProjectMap(this.floors);
    this.clearProjectMap(this.axes);
    this.clearProjectMap(this.cabinets);
    this.clearProjectMap(this.templateGroups);
    //this.sceneGroup.clear();
    this.clearProjectMap(this.sceneGroup);
    this.clearProjectMap(this.addresses);
    this.clearProjectMap(this.topologies);
    this.clearProjectMap(this.cableTypes);
    this.clearProjectMap(this.objectSets);

    //////////////////////////////////////////

    this.objectRegistry.registerObject(this.pageGroups);
    this.objectRegistry.registerObject(this.floors);
    this.objectRegistry.registerObject(this.axes);
    this.objectRegistry.registerObject(this.cabinets);
    this.objectRegistry.registerObject(this.templateGroups);
    this.objectRegistry.registerObject(this.addresses);
    // this.objectRegistry.registerObject(this.sceneGroups);
    this.objectRegistry.registerObject(this.sceneGroup);
    this.objectRegistry.registerObject(this.topologies);
    this.objectRegistry.registerObject(this.cableTypes);
    this.objectRegistry.registerObject(this.objectSets);

    initCableTypes();
    initSignalBundleDefinitions();

    this.taskAndMessageManager.clear();
  }

  getAll(className: string) {
    switch (className) {
      case this.notranslate("Device"): {
        return this.allDevices();
      }
      case this.notranslate("Actor"): {
        return this.allActors();
      }
      case this.notranslate("Button"): {
        return this.allButtons();
      }
      case this.notranslate("Channel"): {
        return this.allActorChannels();
      }
      case this.notranslate("Connection"): {
        return this.allConnections.getAll();
      }
      case this.notranslate("Consumer"): {
        return this.allConsumers();
      }
      case this.notranslate("ELCB"): {
        return this.allELCBs();
      }
      case this.notranslate("Fuse"): {
        return this.allFuses();
      }
      case this.notranslate("Scene"): {
        return this.allScenes();
      }
      case this.notranslate("Room"): {
        return this.allRooms();
      }
      case this.notranslate("Cabinet"): {
        return this.cabinets.asArray();
      }
      case this.notranslate("Floor"): {
        return this.floors.asArray();
      }
      case this.notranslate("Page"): {
        return this.allPages();
      }
      case this.notranslate("TerminalStrips"): {
        return this.allTerminalStrips();
      }
      case this.notranslate("Terminal"): {
        return this.allTerminals();
      }
    }
  }

  getGroupAddress(sAddress: string): GroupAddress | undefined {
    let res: any = undefined;

    let parent: BaseModelMap<GroupAddress> | undefined = this.addresses;

    const sAddresses = sAddress.split("/");

    for (let i = 0; i < sAddresses.length; i++) {
      if (parent) {
        res = parent.findFirst((item) => item.address == sAddresses[i]);
        parent = res?.children;
      }
    }

    return res;
  }

  ///////////////////////////
  /// all blabla

  allTemplates(): Array<DeviceTemplate> {
    const res = new Array<DeviceTemplate>();

    for (const group of this.templateGroups.sortBy("tag")) {
      res.push(...group.allTemplates());
    }

    return res;
  }

  allDeviceSignals(): Array<Signal> {
    const res = new Array<Signal>();

    this.allDevices().forEach((dev) => {
      dev.knxChannels.forEach((channel) => {
        channel.signals.forEach((signal) => {
          const requestSignal = signal.requestSignal;
          if (requestSignal) {
            res.push(requestSignal);
          } else {
            res.push(signal);
          }
        });
      });
    });

    return res;
  }

  allDevices(): Array<Device> {
    return (this.allActors() as Array<Device>).concat(this.allButtons());
  }

  allActors(): Array<Actor> {
    const res = new Array<Actor>();

    this.cabinets.sortBy("tagOrName").forEach((cabinet) => {
      cabinet.actors.sortBy("tagOrName").forEach((actor) => {
        res.push(actor);
      });
    });

    return res;
  }

  allButtons(): Array<Button> {
    const res = new Array<Button>();

    for (const room of this.allRooms()) {
      for (const button of room.allButtons) res.push(button);
    }
    return res;
  }

  allDecentralActors(): Array<Button> {
    return this.allButtons().filter((a) => a.isDecentralActor) as Array<Button>;
  }

  allActorChannels(): Array<Channel> {
    let res = new Array<Channel>();

    this.allActors().forEach((actor) => {
      res = res.concat(actor.channels.asArray());
    });

    this.allButtons().forEach((button) => {
      res = res.concat(button.channels.asArray().filter((channel) => channel.isActorChannel));
    });

    return res;
  }

  allAddresses(): Array<GroupAddress> {
    const res = new Array<GroupAddress>();
    this.addresses.forEach((a1) => {
      a1.children.forEach((a2) => {
        a2.children.forEach((a3) => res.push(a3));
      });
    });

    return res;
  }

  ///////////////////////////////////////////////

  allELCBs(): Array<ELCB> {
    const res = new Array<ELCB>();

    this.cabinets.forEach((cabinet) => {
      cabinet.elcbs.sortBy("tagOrName").forEach((elcb) => {
        res.push(elcb);
      });
    });

    return res;
  }

  allFuses(): Array<Fuse> {
    const res = new Array<Fuse>();

    this.allELCBs().forEach((elcb) => {
      elcb.fuses.sortBy("tagOrName").forEach((fuse) => {
        res.push(fuse);
      });
    });
    state.cabinets.forEach((cabinet) => {
      cabinet.fi_ls_s.forEach((fuse) => {
        res.push(fuse);
      });
    });

    return res;
  }

  /////////////////////////////////////////////////

  ///////////////////////////////////////////////

  allTerminalStrips(): Array<TerminalStrip> {
    const res = new Array<TerminalStrip>();

    this.cabinets.forEach((cabinet) => {
      cabinet.terminalStrips.forEach((terminalStrip) => {
        res.push(terminalStrip);
      });
    });

    return res;
  }

  allTerminals(): Array<Terminal> {
    const res = new Array<Terminal>();

    this.allTerminalStrips().forEach((terminalStrip) => {
      terminalStrip.terminals.forEach((treminal) => {
        res.push(treminal);
      });
    });

    return res;
  }

  allConsumers(): Array<Consumer> {
    const res = new Array<Consumer>();

    for (const room of this.allRooms()) {
      for (const consumer of room.allConsumers()) {
        res.push(consumer);
      }
    }

    return sortArrayByProperty(res, "fullTag");
  }

  allLights(): Array<Consumer> {
    return this.allConsumers().filter((i) => channelTypeInfo(i.channelType).isLight);
  }

  allBlinds(): Array<Consumer> {
    return this.allConsumers().filter((i) => i.channelType == ChannelType.Blind);
  }

  allHeatings(): Array<Consumer> {
    return this.allConsumers().filter((i) => i.channelType == ChannelType.Heating);
  }

  allScenes(): Array<Scene> {
    const res = new Array<Scene>();

    // this.sceneGroups.forEach((sceneGroup) => {
    this.sceneGroup.sortIntBy("sceneNumber").forEach((scene) => {
      res.push(scene);
      scene.sceneNumber;
    });
    //});

    return res;
  }

  allMultiplexers(): Array<Multiplexer> {
    return this.allScenes().filter((scn) => scn.isMultiplexer) as Array<Multiplexer>;
  }

  allRooms(): Array<Room> {
    let res = new Array<Room>();

    this.floors.sortBy("tagOrName").forEach((f) => {
      res = res.concat(f.rooms.asArray());
    });

    return res;
  }

  allPages(): Array<Page> {
    const res = new Array<Page>();
    this.pageGroups.forEach((pg) => {
      const children = pg.sortedPages;
      children.forEach((child) => {
        if (child.className == "PageReference") {
          const page = (child as PageReference).referencedPage;
          if (page) {
            res.push(page);
          }
        } else {
          const page = child as Page;
          if (page) {
            res.push(page);
          }
        }
      });
    });
    return res;
  }
  allPagesWithBackgroundUsages(): Array<Page> {
    const res = new Array<Page>();
    this.allPages().forEach((page) => {
      if (page.isBackgroundImageFromFloorPlan || page.backgroundImgUrl) {
        res.push(page);
      }
    });
    return res;
  }

  allRoomPages(): Map<string, Page> {
    const res = new Map<string, Page>();

    this.floors.forEach((floor) => {
      floor.rooms.forEach((room) => {
        if (room.page) {
          res.set(room.page.id, room.page);
        }
      });
    });
    return res;
  }
  allFloorPags(): Map<string, Page> {
    const res = new Map<string, Page>();

    this.floors.forEach((floor) => {
      if (floor.page) {
        res.set(floor.page.id, floor.page);
      }
    });
    return res;
  }
  allGroupAddresses(onlyLevel3: boolean): Array<GroupAddress> {
    const res = new Array<GroupAddress>();
    function getChildrenGroupAdresses(adr: GroupAddress) {
      if (adr.children.size > 0) {
        adr.children.forEach((child) => {
          getChildrenGroupAdresses(child);
        });
      } else {
        if (!onlyLevel3 || adr.level == 3) {
          res.push(adr);
        }
      }
    }
    state.addresses.forEach((adress) => {
      getChildrenGroupAdresses(adress);
    });
    return res;
  }

  allLevel3GroupAddresses(): Array<GroupAddress> {
    let res = new Array<GroupAddress>();

    this.addresses.forEach((a1) => {
      a1.children.forEach((a2) => {
        res = res.concat(a2.children.asArray());
      });
    });

    return res;
  }

  allSignals(): Array<Signal> {
    const res = new Array<Signal>();

    this.allDevices().forEach((device) => {
      device.channels.forEach((channel) => {
        channel.signals.forEach((signal) => {
          res.push(signal);
        });
      });
    });

    return res;
  }

  allConsumerSignals(): Array<Signal> {
    const res = new Array<Signal>();
    this.allConsumers().forEach((consumer) => {
      consumer.signals.forEach((signal) => {
        res.push(signal);
      });
    });
    return res;
  }

  allMultiplexerSignals(): Array<Signal> {
    const res = new Array<Signal>();
    this.allMultiplexers().forEach((multiplexer) => {
      multiplexer.signals.forEach((signal) => {
        res.push(signal);
      });
    });
    return res;
  }

  allGroupAddressesReferencesFromDevices(): Array<GroupAddress> {
    const res = new Array<GroupAddress>();

    this.allDevices().forEach((device) => {
      device.channels.forEach((channel) => {
        channel.signals.forEach((signal) => {
          signal.groupAddresses.forEach((groupAdress) => {
            res.push(groupAdress);
          });
        });
      });
    });

    return res;
  }

  updateAllConnectionsWithoutUpdate() {
    this.allConnections.getAll().forEach((connection) => {
      connection.addToConnectableObjectsWithoutUpdate();
    });
  }

  hostBaseUrl = "/";

  get loggedInUser(): any | undefined {
    try {
      const userAsString = localStorage.getItem("user");
      if (userAsString == "") {
        return undefined;
      }
      let user = JSON.parse(userAsString as string);
      // to get the name out og the Email Adress
      if (user.abo) {
        // argument order must match aboValid(abo, abo_status, abo_end)
        if (!AboHandeling.aboValid(user.abo, user.abo_status, user.abo_end)) {
          user.abo = "no";
        }
      }
      if (user.email && !user.name) {
        let name = user.email.substring(0, user.email.indexOf("@"));
        const splittedName = name.split(".");
        name = "";
        splittedName.forEach((part: string) => {
          if (name != "") {
            name = name + " ";
          }
          name = name + part[0].toUpperCase() + part.slice(1);
        });

        user = { ...user, name: name };
      }
      return user;
    } catch (ex) {
      return undefined;
    }
  }

  get profilePicture(): any | undefined {
    try {
      // so that other Acounts can have other ProfilePictures
      return JSON.parse(localStorage.getItem(`profilePicture${state.loggedInUser?.id || ""} `) as string);
    } catch (ex) {
      return undefined;
    }
  }
  set profilePicture(newValue: any | undefined) {
    try {
      // so that other Acounts can have other ProfilePictures
      localStorage.setItem(`profilePicture${state.loggedInUser?.id || ""} `, newValue);
    } catch (ex) {}
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////
  //// onboarding

  debugOpendOnboardingKeys: Array<string> = [];
  get onBoardingSeenKeys(): Array<string> {
    try {
      // so that other Acounts can have other ProfilePictures

      let x = JSON.parse(localStorage.getItem(`onBoardingSeenKeys`) as string);
      x ||= [];
      this.debugOpendOnboardingKeys = x;
      return x;
    } catch (ex) {
      return [];
    }
  }
  set onBoardingSeenKeys(newValue: Array<string>) {
    try {
      // so that other Acounts can have other ProfilePictures
      const filteredArray = newValue?.filter((el, index) => {
        return el != null && el != undefined && el != "" && newValue.indexOf(el) == index;
      });

      this.debugOpendOnboardingKeys = filteredArray;
      localStorage.setItem(`onBoardingSeenKeys`, JSON.stringify(filteredArray));
    } catch (ex) {}
  }

  // opendOnboardingKeys: Set<string> = new Set<string>();

  /////////////////////////////////////////////////////////////////
  //
  // commands are routed/send to all "useCommand (...)"" in any component
  //

  commandBus = new Event();
  command(name: string, data?: any) {
    this.commandBus.raise(name, data);
  }
  //////////////////////////////////////////////////////////////////

  gptEvents = new WatchableValue<string>("");

  serverTemplatelistLoaded = new WatchableValue<boolean>(false);

  navigateToId(id: string) {
    const obj = this.objectRegistry.getObject(id);

    if (obj) {
      this.selectionManager.selectedObject = obj as BaseModel;
    }
  }

  lastCreatedObjectForGuide: ConnectableObject | undefined;

  // remembers the last selected tab of a properties view, keyed by the shown object's id,
  // so switching between different devices/consumers/etc. restores each one's own last tab
  lastPropertiesTabByObjectId = new Map<string, string>();

  getLastPropertiesTab(objectId: string): string | undefined {
    return this.lastPropertiesTabByObjectId.get(objectId);
  }

  setLastPropertiesTab(objectId: string, tabId: string) {
    this.lastPropertiesTabByObjectId.set(objectId, tabId);
  }

  //
  // MWE 15.10.2024
  //     move uiMode back to state to refactor it to a watchable value
  //
  uiMode = new WatchableValue<enumUiMode>(
    parseInt(localStorage.getItem("uiMode") || `${enumUiMode.user} `) as enumUiMode,
  );
  setUiMode(newValue: enumUiMode) {
    setUiMode_XX(newValue);
    this.uiMode.value = newValue;
    this.connectionDisplayMode.value = ConnectionDisplayModes.all;

    if (!isUiModeProductAdmin()) {
      const closeTopologyLinePanes = (activePanes: ActivePanes | undefined) => {
        activePanes?.items
          .filter((pane) => pane.item === this.topologies || pane.item?.className === "Line")
          .forEach((pane) => activePanes.closePane(pane.id, pane.subPageId));
      };

      closeTopologyLinePanes(this.activePanes);
      closeTopologyLinePanes(this.activePanes2);
    }

    const appElement = document.getElementById("App");
    if (isUiModeProductAdmin()) {
      appElement?.classList.add("productAdminMode");
    } else {
      appElement?.classList.remove("productAdminMode");
    }

    if (isUiModeExpert() || isUiModeProductAdmin()) {
      appElement?.classList.add("expertMode");
    } else {
      appElement?.classList.remove("expertMode");
    }
  }

  events: Lo | undefined;
  track(text: string) {
    try {
      if (this.events) {
        this.events.track(text);
      }
    } catch (ex) {}
  }

  _showModifiedStatus = new WatchableValue(false);
  _manualShowModifiedStatus = new WatchableValue<unknown | boolean>(undefined);

  public get manualSetShowModifiedStatus() {
    return this._manualShowModifiedStatus.value;
  }
  public set manualSetShowModifiedStatus(value) {
    this._manualShowModifiedStatus.value = value;
    if (typeof value === "boolean") {
      this.showModifiedStatus = value;
    }
  }

  public get showModifiedStatus() {
    if (this.manualSetShowModifiedStatus !== undefined) {
      return this.manualSetShowModifiedStatus as boolean;
    }
    return this._showModifiedStatus.value;
  }
  public set showModifiedStatus(value) {
    if (value) {
      this.calcDirtyChilds();
    }
    this._showModifiedStatus.value = value;
    BaseModel.monitorDelete = value;
  }

  //
  // this code kills a lot of basic functionality,
  // like isDirty Monitoring
  //
  // Use with care and ONLY during development
  //
  clearModifiedStatus() {
    this.floors.clearModifiedStatus();
    this.axes.clearModifiedStatus();
    this.cabinets.clearModifiedStatus();
    this.templateGroups.clearModifiedStatus();
    this.addresses.clearModifiedStatus();
    this.sceneGroup.clearModifiedStatus();
    this.pageGroups.clearModifiedStatus();
    this.cableTypes.clearModifiedStatus();

    this.objectRegistry.allObjects.forEach((obj) => {
      obj.clearModifiedStatus();
    });
  }

  calcDirtyChilds() {
    this.objectRegistry.allObjects.forEach((obj) => {
      obj.dirtyChilds = false;
    });

    this.objectRegistry.allObjects.forEach((obj) => {
      if (obj.isDirty && !obj.isDeleted) {
        let p = obj.parent;
        while (p) {
          if (p.dirtyChilds) {
            return; // from forEach
          } else {
            p.dirtyChilds = true;
            p.raiseOnChange("dirtyChilds", undefined, true);
            p = p.parent;
          }
        }
      }
    });
  }

  getActivePage(): Page | undefined {
    let id = "";

    if (this.activeDocumentScreen == 1) {
      id = this.activePanes.getActiveId();
    } else {
      id = this.activePanes2?.getActiveId() || "";
    }

    const page = state.objectRegistry.getObject(id);
    if (page?.className == "Page") {
      return page as Page;
    }

    return undefined;
  }

  deleteAllGroupAddresses() {
    //
    // dont set  this.addresses.lastItems
    //
    this.addresses.items.clear();
    this.addresses.lastItems?.clear();

    this.addresses.raiseOnChange("childCollection");

    this.allDevices().forEach((device) => {
      device.channels.forEach((channel) => {
        channel.signals.forEach((signal) => {
          signal.groupAddresses.clear();
          signal.lastGroupAddresses?.clear();
          signal.raiseOnChange("groupAddresses");
        });
      });
    });
  }

  updateAreas(updateLines: boolean): Array<Area> {
    const areaSet = new Set<string>();

    this.allDevices().forEach((device) => {
      if (!channelTypeInfo(device.channelType).isSupply) {
        const s = device.areaAddress;
        if (s != "") {
          areaSet.add(s);
        }
      }
    });

    //
    // remove unused areas
    //
    this.topologies.forEach((topology) => {
      if (!areaSet.has(String(topology.areaNumber))) {
        this.settings.clearTopologyAreaNames(topology.areaNumber);
        state.topologies.removeWithoutUpdate(topology);
      }
    });

    //
    // add new areas
    //

    areaSet.forEach((areaNumber) => {
      const allreadyExists = this.topologies.findFirst((area) => String(area.areaNumber) == areaNumber);
      if (!allreadyExists) {
        this.topologies.addWithoutUpdate(
          new Area({ _tag: areaNumber, areaNumber: parseInt(areaNumber) }, state.topologies),
        );
      }
    });

    if (updateLines) {
      state.topologies.forEach((area) => {
        area.updateLines();
      });
    }

    return state.topologies.sortIntBy("tag");
  }

  openOrActivatePane(paneId: string) {
    if (state.activePanes.isOpen(paneId)) {
      state.activePanes.activatePane(paneId);
    } else {
      state.activePanes.loadPage(paneId);
    }
  }

  scriptOutput = new WatchableValue<string>("");
  scriptOutputFilterSignalNumber = new WatchableValue<string>("");

  debugOutput = new DebugOutput("");
  debugData?: {
    signalRuleIdToAnalyse?: string;
    signalNumberToAnalyse?: string;
  };

  estadaBridgeIsRunning = false;
  justTiedToStartEstadaBridge = false;
}

// createDemoData(globalThis.state);

if (window.opener) {
  console.log("ignore create State for second screen");

  globalThis.state = (window.opener as any)._state;
  (window as any)._state = (window.opener as any)._state;
} else {
  console.log("create State");

  (window as any)._state = new State();

  logger.log("url ", navigator);
}
